import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import { adminApi } from "../../services/api";
import { Softphone } from "@frejun/softphone-web-sdk";

const STATUS_OPTIONS = [
  "new_lead", "pending", "called", "follow_up", "reached", "not_interested",
  "rejected", "forwarded", "account_made", "closed",
  "underage", "ftd", "call_back", "no_answer", "no_language",
];

const PAGE_SIZE = 40;

const TeamLeadPanel = () => {
  const dispatch = useDispatch();
  const { adminUser } = useSelector((state) => state.admin);

  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Team popup
  const [showTeamPopup, setShowTeamPopup] = useState(false);

  // Assign down
  const [selectedStaff, setSelectedStaff] = useState({});
  const [assigning, setAssigning] = useState({});

  // Lead editing
  const [expandedLead, setExpandedLead] = useState(null);
  const [statusInputs, setStatusInputs] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [updating, setUpdating] = useState({});

  // User Account Creation
  const [passwordInputs, setPasswordInputs] = useState({});
  const [showPasswordField, setShowPasswordField] = useState({});
  const [converting, setConverting] = useState({});

  // Move to Retention
  const [confirmMove, setConfirmMove] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Search + filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Bulk assign to team
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [selectedBulkStaff, setSelectedBulkStaff] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkCount, setBulkCount] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  // ─── EMAIL STATE ───
  const [emailSenderOptions, setEmailSenderOptions] = useState([]);
  const [showSenderSelect, setShowSenderSelect] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [composeLeadName, setComposeLeadName] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [targetLeadEmail, setTargetLeadEmail] = useState(null);
  const fileInputRef = useRef(null);

  // ─── INBOX STATE ───
  const [showInbox, setShowInbox] = useState(false);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [selectedEmailBody, setSelectedEmailBody] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState("");

  // ─── FREJUN SOFT PHONE STATE ───
  const [isCallActive, setIsCallActive] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null); 
  const softphoneRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localAudioRef = useRef(null);
  const incomingTimeoutRef = useRef(null);
  const ringtoneRef = useRef(null);

  // ─── CALL HISTORY STATE ───
  const [callHistory, setCallHistory] = useState([]);
  const [showCallHistory, setShowCallHistory] = useState(false);

  const FREJUN_TOKEN = import.meta.env.VITE_FREJUN_TOKEN;
  const FREJUN_EMAIL =
    adminUser?.frejunEmail ||
    import.meta.env.VITE_FREJUN_EMAIL ||
    "finance@groxion.com";
  const AGENT_VIRTUAL_NUMBER = adminUser?.frejunNumber || null;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [leadsRes, teamRes] = await Promise.all([
        adminApi.get("/leads/team-lead/my"),
        adminApi.get("/leads/team-lead/my-team"),
      ]);
      setLeads(leadsRes.data?.leads || []);
      setMembers(teamRes.data?.members || []);
    } catch (e) {
      dispatch(
        showToast({ message: "Failed to load your team data", type: "error" }),
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
    fetchEmailCredentials();
  }, [load]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLeadIds(new Set());
    setSelectAll(false);
  }, [statusFilter, assignedFilter, searchQuery]);

  // Esc closes modals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (confirmMove && !confirmLoading) setConfirmMove(null);
        if (showTeamPopup) setShowTeamPopup(false);
        if (showCallHistory) setShowCallHistory(false);
        if (showInbox) setShowInbox(false);
        if (showComposeEmail) setShowComposeEmail(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmMove, confirmLoading, showTeamPopup, showCallHistory, showInbox, showComposeEmail]);

  // ─── RINGTONE SETUP & UNLOCK ───
  useEffect(() => {
    ringtoneRef.current = new Audio("/the_fire_love.mp3");
    ringtoneRef.current.loop = true;
    ringtoneRef.current.preload = "auto";

    const unlockAudio = () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.volume = 0;
        ringtoneRef.current
          .play()
          .then(() => {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
            ringtoneRef.current.volume = 1;
            console.log("[Audio] Ringtone unlocked successfully");
          })
          .catch((e) => console.warn("[Audio] Unlock blocked by browser:", e));
      }
      document.removeEventListener("click", unlockAudio);
    };

    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, []);

  const playRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch((e) => {
        console.error("[Audio] Play failed:", e);
        dispatch(
          showToast({
            message: "Please click anywhere on the screen to allow ringtone audio.",
            type: "warning",
          }),
        );
      });
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  const clearIncoming = () => {
    if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current);
    incomingTimeoutRef.current = null;
    stopRingtone();
    setIncomingCall(null);
  };

  // ─── INIT FREJUN SOFT PHONE ───
  useEffect(() => {
    if (!FREJUN_TOKEN || !adminUser?.frejunEmail) return;

    let isMounted = true;
    const initSoftphone = async () => {
      try {
        const softphone = new Softphone();
        await softphone.login({
          type: "OAuth2.0",
          token: FREJUN_TOKEN,
          email: FREJUN_EMAIL,
        });
        if (!isMounted) return;
        softphoneRef.current = softphone;

        const listeners = {
          onConnectionStateChange: (type, newState) => {},
          onCallCreated: (type, details) => {
            if (type === "Incoming") {
              setIncomingCall({ lead: null });
              playRingtone();

              let callerNumber = details?.candidate || details?.request?.from?.uri?.user || "";
              if (callerNumber.includes("sip:")) {
                callerNumber = callerNumber.split("sip:")[1].split("@")[0];
              }

              console.log("Extracted Caller Number:", callerNumber);

              const historyId = Date.now();
              setCallHistory((prev) => [
                {
                  id: historyId,
                  leadName: "Unknown Caller",
                  shortId: null,
                  timestamp: new Date().toLocaleString(),
                  status: "Unknown",
                },
                ...prev.slice(0, 19),
              ]);

              if (incomingTimeoutRef.current)
                clearTimeout(incomingTimeoutRef.current);
              incomingTimeoutRef.current = setTimeout(
                () => setIncomingCall(null),
                60000,
              );

              if (callerNumber) {
                adminApi
                  .post("/leads/lookup-phone", { phone: callerNumber })
                  .then((res) => {
                    console.log("Lookup Response:", res.data);
                    if (res.data?.found) {
                      setIncomingCall((prev) =>
                        prev ? { lead: res.data.lead } : prev,
                      );
                      setCallHistory((prev) =>
                        prev.map((h) =>
                          h.id === historyId
                            ? {
                                ...h,
                                leadName: res.data.lead.name,
                                shortId: res.data.lead.short_id,
                                status: "Lead Matched",
                              }
                            : h,
                        ),
                      );
                    }
                  })
                  .catch((err) => console.error("Lookup Failed:", err));
              }
            } else if (type === "Outgoing") {
              setIsCallActive(true);
            }
          },
          onCallRinging: () => {},
          onCallHangup: () => {
            setIsCallActive(false);
            clearIncoming();
          },
        };

        const audioElements = {
          remote: remoteAudioRef.current,
          local: localAudioRef.current,
        };
        await softphone.start(listeners, audioElements, {
          endSessionOnNavigate: false,
          logoutOnNavigate: false,
        });
      } catch (err) {
        console.error("[Softphone] init failed:", err);
        if (isMounted)
          dispatch(
            showToast({
              message: "Dialer init failed. Check token/email.",
              type: "error",
            }),
          );
      }
    };

    initSoftphone();
    return () => {
      isMounted = false;
      softphoneRef.current?.logout();
      softphoneRef.current = null;
      stopRingtone();
    };
  }, [FREJUN_TOKEN, FREJUN_EMAIL, adminUser?.frejunEmail, dispatch]);

  const handleFrejunCall = async (phoneNumber, leadName) => {
    if (!phoneNumber || !softphoneRef.current) {
      dispatch(
        showToast({ message: "Dialer not ready. Please wait.", type: "error" }),
      );
      return;
    }
    try {
      setIsCallActive(true);
      await softphoneRef.current.makeCall(
        phoneNumber,
        AGENT_VIRTUAL_NUMBER || undefined,
        {},
      );
    } catch (err) {
      console.error("[Softphone] makeCall failed:", err);
      setIsCallActive(false);
      dispatch(showToast({ message: "Call failed.", type: "error" }));
    }
  };

  const acceptCall = async () => {
    stopRingtone();
    try {
      await softphoneRef.current?.getSession?.accept();
      setIncomingCall(null);
      setIsCallActive(true);
    } catch (err) {
      console.error("[Softphone] accept failed:", err);
      dispatch(showToast({ message: "Accept failed", type: "error" }));
    }
  };

  const declineCall = async () => {
    stopRingtone();
    clearIncoming();
    setIsCallActive(false);
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
  };

  const handleHangup = async () => {
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
    setIsCallActive(false);
  };

  // ─── Team Lead display name ───────────────────────────────────────
  const tlName = adminUser
    ? `${adminUser.firstName || ""} ${adminUser.lastName || ""}`.trim()
    : "";

  // ─── Assign-down (single lead) ─────────────────────────────────────
  const handleAssignDown = async (leadId) => {
    const staffId = selectedStaff[leadId];
    if (!staffId) {
      dispatch(
        showToast({ message: "Select a team member first", type: "error" }),
      );
      return;
    }
    setAssigning((prev) => ({ ...prev, [leadId]: true }));
    try {
      await adminApi.put(`/leads/${leadId}/assign-to-team`, { staffId });
      dispatch(
        showToast({ message: "Lead assigned to team member", type: "success" }),
      );
      setSelectedStaff((prev) => {
        const next = { ...prev };
        delete next[leadId];
        return next;
      });
      await load();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to assign",
          type: "error",
        }),
      );
    } finally {
      setAssigning((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  // ─── Update lead status ───────────────────────────────────────────
  const handleUpdateLead = async (leadId) => {
    const status = statusInputs[leadId] || "";
    const comment = commentInputs[leadId] || "";
    if (!status && !comment) {
      dispatch(
        showToast({ message: "Add a status or comment", type: "error" }),
      );
      return;
    }
    setUpdating((prev) => ({ ...prev, [leadId]: true }));
    try {
      const res = await adminApi.put(`/leads/${leadId}/status`, {
        status: status || null,
        comment,
      });
      if (res.data?.lead) {
        setLeads((prev) =>
          prev.map((l) => (l.id === leadId ? res.data.lead : l)),
        );
      }
      dispatch(showToast({ message: "Lead updated", type: "success" }));
      setStatusInputs((prev) => ({ ...prev, [leadId]: "" }));
      setCommentInputs((prev) => ({ ...prev, [leadId]: "" }));
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to update",
          type: "error",
        }),
      );
    } finally {
      setUpdating((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  // ─── User Account Creation ────────────────────────────────────────
  const handleConvertToUser = async (leadId) => {
    const password = passwordInputs[leadId] || "";
    if (!password || password.length < 6) {
      dispatch(
        showToast({
          message: "Password must be at least 6 characters",
          type: "error",
        }),
      );
      return;
    }
    setConverting((prev) => ({ ...prev, [leadId]: true }));
    try {
      const res = await adminApi.post(`/leads/${leadId}/convert`, { password });
      dispatch(
        showToast({
          message: `Account created! User ID: ${res.data.lead?.short_id}`,
          type: "success",
        }),
      );
      setPasswordInputs((prev) => ({ ...prev, [leadId]: "" }));
      setShowPasswordField((prev) => ({ ...prev, [leadId]: false }));
      await load();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to create account",
          type: "error",
        }),
      );
    } finally {
      setConverting((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  const handleResetPassword = async (leadId) => {
    const password = passwordInputs[leadId] || "";
    if (!password || password.length < 6) {
      dispatch(
        showToast({
          message: "Password must be at least 6 characters",
          type: "error",
        }),
      );
      return;
    }
    setConverting((prev) => ({ ...prev, [leadId]: true }));
    try {
      await adminApi.put(`/leads/${leadId}/password`, { password });
      dispatch(
        showToast({
          message: "Password updated successfully",
          type: "success",
        }),
      );
      setPasswordInputs((prev) => ({ ...prev, [leadId]: "" }));
      setShowPasswordField((prev) => ({ ...prev, [leadId]: false }));
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to update password",
          type: "error",
        }),
      );
    } finally {
      setConverting((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  // ─── Move to Retention / Conversion ───────────────────────────────
  const requestSetLeadType = (lead, targetType) => {
    if ((lead.lead_type || "conversion") === targetType) return;
    setConfirmMove({ leadId: lead.id, targetType });
  };

  const handleSetLeadType = async (leadId, leadType) => {
    try {
      await adminApi.put(`/leads/${leadId}/type`, { leadType });
      dispatch(
        showToast({ message: `Lead moved to ${leadType}`, type: "success" }),
      );
      await load();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to move lead",
          type: "error",
        }),
      );
    }
  };

  const confirmMoveLead = async () => {
    if (!confirmMove) return;
    setConfirmLoading(true);
    await handleSetLeadType(confirmMove.leadId, confirmMove.targetType);
    setConfirmLoading(false);
    setConfirmMove(null);
  };

  // Triggers FreJun Dialer securely
  const handleDialNumber = async (leadId, e) => {
    e.stopPropagation();
    if (!AGENT_VIRTUAL_NUMBER) {
      dispatch(
        showToast({
          message: "No virtual number assigned to your account. Contact Admin.",
          type: "error",
        }),
      );
      return;
    }

    try {
      const res = await adminApi.get(`/leads/${leadId}/dial`);
      if (res.data?.phone) {
        const lead = leads.find((l) => l.id === leadId);
        const leadName = lead ? lead.name : "Unknown";
        handleFrejunCall(res.data.phone, leadName);
      }
    } catch (e) {
      dispatch(showToast({ message: "No phone number found", type: "error" }));
    }
  };

  // ─── EMAIL HANDLERS ───
  const fetchEmailCredentials = async () => {
    try {
      const res = await adminApi.get("/admin/email/credentials");
      setEmailSenderOptions(res.data?.emailOptions || []);
    } catch (e) {}
  };

  const handleOpenSenderSelect = (leadId, leadEmail, leadName, e) => {
    e.stopPropagation();
    if (!emailSenderOptions || emailSenderOptions.length === 0) {
      dispatch(showToast({ message: "No email accounts assigned.", type: "error" }));
      return;
    }
    setTargetLeadEmail({ id: leadId, email: leadEmail, name: leadName });
    setShowSenderSelect(true);
  };

  const handleSelectSender = (sender) => {
    setSelectedSender(sender);
    setShowSenderSelect(false);
    setComposeLeadName(targetLeadEmail.name);
    setComposeSubject("");
    setComposeBody("");
    setAttachments([]);
    setShowComposeEmail(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.type.startsWith('image/') || f.type === 'application/pdf');
    const newAttachments = validFiles.map(f => ({
      name: f.name,
      type: f.type,
      size: (f.size / 1024).toFixed(1) + ' KB',
      file: f
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    if (!composeLeadName || !composeSubject || !composeBody || !selectedSender) {
      dispatch(showToast({ message: "All fields are required", type: "error" }));
      return;
    }
    setSendingEmail(true);
    try {
      const attachmentsText = attachments.length > 0 
        ? `\n\n--- Attachments ---\n${attachments.map(a => `${a.name} (${a.type})`).join('\n')}` 
        : '';

      await adminApi.post("/admin/email/send", {
        to: targetLeadEmail.email,
        subject: composeSubject,
        body: composeBody + attachmentsText,
        senderId: selectedSender.id, 
      });
      dispatch(showToast({ message: "Email sent successfully", type: "success" }));
      setShowComposeEmail(false);
      setComposeLeadName(""); setComposeSubject(""); setComposeBody(""); setSelectedSender(null); setAttachments([]);
    } catch (e) {
      dispatch(showToast({ message: e.response?.data?.message || "Failed to send email", type: "error" }));
    } finally {
      setSendingEmail(false);
    }
  };

  // ─── INBOX HANDLERS ───
  const handleOpenInbox = async () => {
    setShowInbox(true);
    if (emailSenderOptions.length === 0) return;
    
    const defaultEmail = emailSenderOptions[0].email;
    setSelectedAccount(defaultEmail);
    loadInbox(defaultEmail);
  };

  const loadInbox = async (email) => {
    if (!email) return;
    setInboxLoading(true);
    setSelectedEmailBody(null);
    try {
      const res = await adminApi.get(`/admin/email/inbox?email=${email}`);
      const rawEmails = res.data?.emails || [];
      
      const enriched = await Promise.all(rawEmails.map(async (em) => {
        try {
          const lookup = await adminApi.post("/leads/lookup-email", { email: em.from });
          return { ...em, lead: lookup.data.found ? lookup.data.lead : null };
        } catch (e) {
          return { ...em, lead: null };
        }
      }));

      setInboxEmails(enriched.filter(em => em.lead !== null));
    } catch (e) {
      dispatch(showToast({ message: "Failed to load inbox", type: "error" }));
    } finally {
      setInboxLoading(false);
    }
  };

  const handleViewEmailBody = async (uid) => {
    try {
      const res = await adminApi.get(`/admin/email/body/${uid}?email=${selectedAccount}`);
      setSelectedEmailBody(res.data);
    } catch (e) {
      dispatch(showToast({ message: "Failed to load email body", type: "error" }));
    }
  };


  // ─── Filtering + pagination ────────────────────────────────────────
  const getFilteredLeads = () => {
    let filtered = leads;

    if (statusFilter !== "all")
      filtered = filtered.filter((l) => l.status === statusFilter);

    if (assignedFilter === "unassigned") {
      filtered = filtered.filter((l) => !l.assignedTo);
    } else if (assignedFilter !== "all") {
      filtered = filtered.filter((l) => l.assignedTo === assignedFilter);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(
        (l) =>
          (l.name && l.name.toLowerCase().includes(q)) ||
          (l.email && l.email.toLowerCase().includes(q)) ||
          (l.phone && l.phone.toLowerCase().includes(q)) ||
          (l.short_id && l.short_id.toLowerCase().includes(q)),
      );
    }

    return filtered;
  };

  const displayLeads = getFilteredLeads();
  const totalPages = Math.ceil(displayLeads.length / PAGE_SIZE);
  const paginatedLeads = displayLeads.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  // ─── Bulk assign to my team ────────────────────────────────────────
  const toggleBulkStaff = (id) => {
    setSelectedBulkStaff((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleLead = (leadId) => {
    const next = new Set(selectedLeadIds);
    if (next.has(leadId)) next.delete(leadId);
    else next.add(leadId);
    setSelectedLeadIds(next);
    setSelectAll(
      paginatedLeads.length > 0 && paginatedLeads.every((l) => next.has(l.id)),
    );
  };

  const handleSelectAllPage = () => {
    if (selectAll) {
      setSelectedLeadIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedLeadIds(new Set(paginatedLeads.map((l) => l.id)));
      setSelectAll(true);
    }
  };

  const getLeadsToAssign = () => {
    if (selectedLeadIds.size > 0) {
      return leads.filter((l) => selectedLeadIds.has(l.id));
    }
    return paginatedLeads;
  };

  const getLeadsToAssignCount = () => {
    const pool = getLeadsToAssign();
    if (bulkCount && Number(bulkCount) > 0) {
      return Math.min(Number(bulkCount), pool.length);
    }
    return pool.length;
  };

  const handleBulkAssign = async () => {
    const pool = getLeadsToAssign();
    if (pool.length === 0) {
      dispatch(showToast({ message: "Select leads first", type: "error" }));
      return;
    }
    if (selectedBulkStaff.length === 0) {
      dispatch(
        showToast({
          message: "Select at least one team member",
          type: "error",
        }),
      );
      return;
    }

    let leadIds = pool.map((l) => l.id);
    if (bulkCount && Number(bulkCount) > 0) {
      leadIds = leadIds.slice(0, Number(bulkCount));
    }

    if (
      !confirm(
        `Assign ${leadIds.length} lead(s) evenly among ${selectedBulkStaff.length} team member(s)?`,
      )
    )
      return;

    setBulkAssigning(true);
    try {
      const res = await adminApi.post("/leads/team-lead/bulk-assign", {
        leadIds,
        staffIds: selectedBulkStaff,
      });
      dispatch(
        showToast({
          message: `${res.data?.assigned || 0} lead(s) assigned`,
          type: "success",
        }),
      );
      setSelectedLeadIds(new Set());
      setSelectAll(false);
      setSelectedBulkStaff([]);
      setBulkCount("");
      setShowBulkAssign(false);
      await load();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Bulk assign failed",
          type: "error",
        }),
      );
    } finally {
      setBulkAssigning(false);
    }
  };

  // ─── UI helpers ───────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      new_lead: "bg-[#059669] text-white",
      pending: "bg-[#d97706] text-white",
      called: "bg-[#2563eb] text-white",
      follow_up: "bg-[#7c3aed] text-white",
      reached: "bg-[#0d9488] text-white",
      not_interested: "bg-[#64748b] text-white",
      rejected: "bg-[#dc2626] text-white",
      forwarded: "bg-[#1e3a5f] text-white",
      account_made: "bg-[#059669] text-white",
      underage: "bg-[#dc2626] text-white",
      ftd: "bg-[#0d9488] text-white",
      call_back: "bg-[#d97706] text-white",
      no_answer: "bg-[#d97706] text-white",
      no_language: "bg-[#ec4899] text-white",
      closed: "bg-[#64748b] text-white",
    };
    const label =
      status === "new_lead"
        ? "NEW LEAD"
        : status
          ? status.replace(/_/g, " ").toUpperCase()
          : "NEW LEAD";
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${colors[status] || "bg-[#94a3b8] text-white"}`}
      >
        {label}
      </span>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isRetention = adminUser?.staffType === "retention";

  const Pagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return (
      <div className="flex items-center justify-between px-5 py-3.5 border-t-2 border-[#e2e8f0] bg-[#f8fafc]">
        <span className="text-[11px] text-[#475569] font-mono font-extrabold">
          Showing {(currentPage - 1) * PAGE_SIZE + 1}–
          {Math.min(currentPage * PAGE_SIZE, displayLeads.length)} of{" "}
          <span className="text-[#0f172a]">{displayLeads.length}</span> leads
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30"
          >
            <i className="fas fa-angles-left text-[9px]" />
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30"
          >
            <i className="fas fa-chevron-left text-[9px]" />
          </button>
          {pages.map((p) => (
            <button
              key={p}
              onClick={() => goToPage(p)}
              className={`w-8 h-8 rounded-lg text-[11px] font-mono font-extrabold transition-all ${currentPage === p ? "bg-[#1e3a5f] text-white border-2 border-[#1e3a5f] shadow-md" : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#1e3a5f] hover:border-[#1e3a5f]"}`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30"
          >
            <i className="fas fa-chevron-right text-[9px]" />
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[11px] font-extrabold flex items-center justify-center hover:text-[#1e3a5f] hover:border-[#1e3a5f] disabled:opacity-30"
          >
            <i className="fas fa-angles-right text-[9px]" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <audio ref={remoteAudioRef} autoPlay />
      <audio ref={localAudioRef} autoPlay muted />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div className="flex items-center gap-3">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${isRetention ? "bg-gradient-to-br from-[#d97706] to-[#b45309] shadow-[#d97706]/25" : "bg-gradient-to-br from-[#059669] to-[#047857] shadow-[#059669]/25"}`}
          >
            <i className="fas fa-user-tie text-white text-lg" />
          </div>
          <div>
            <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
              MY TEAM LEADS DESK
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {isRetention ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#fef3c7] text-[#92400e] text-[9px] font-mono font-extrabold uppercase tracking-wider border border-[#fcd34d]">
                  <i className="fas fa-lock text-[8px]" /> RETENTION TL
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#ecfdf5] text-[#059669] text-[9px] font-mono font-extrabold uppercase tracking-wider border border-[#a7f3d0]">
                  <i className="fas fa-rotate text-[8px]" /> CONVERSION TL
                </span>
              )}
              <span className="text-[10px] text-[#475569] font-mono font-bold">
                {leads.length} lead{leads.length !== 1 ? "s" : ""} ·{" "}
                {members.length} team member{members.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTeamPopup(true)}
            className="px-4 py-2 rounded-xl bg-[#0d9488] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0f766e] transition-all border-b-[2px] border-[#0f766e]"
          >
            <i className="fas fa-users mr-1.5" /> SHOW TEAM ({members.length})
          </button>
          <button
            onClick={load}
            className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
          >
            <i className="fas fa-sync-alt mr-1.5" /> REFRESH
          </button>
        </div>
      </div>

      {/* LEADS SECTION */}
      <div className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-syne text-sm font-extrabold text-[#0b1e33] uppercase tracking-tight">
              MY LEADS
            </h3>
            <p className="text-[10px] text-[#475569] font-mono font-bold mt-1">
              {displayLeads.length} lead{displayLeads.length !== 1 ? "s" : ""}
              {selectedLeadIds.size > 0
                ? ` · ${selectedLeadIds.size} selected`
                : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94a3b8] text-[11px]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search leads..."
                className="w-56 bg-white border-2 border-[#cbd5e1] rounded-xl py-2.5 pl-10 pr-4 text-[#0f172a] text-[11px] font-extrabold outline-none focus:border-[#1e3a5f] placeholder:text-[#94a3b8]"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] cursor-pointer"
            >
              <option value="all">ALL STATUSES</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ").toUpperCase()}
                </option>
              ))}
            </select>
            <select
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e.target.value)}
              className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] cursor-pointer"
            >
              <option value="all">ALL LEADS</option>
              <option value="unassigned">
                {tlName ? `${tlName} (TL)` : "TEAM LEAD (TL)"}
              </option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowBulkAssign((v) => !v)}
              className="px-4 py-2.5 rounded-xl bg-[#7c3aed] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#6d28d9] transition-all border-b-[2px] border-[#6d28d9]"
            >
              <i className="fas fa-users-gear mr-1.5" /> BULK TO TEAM
            </button>
          </div>
        </div>

        {/* Bulk assign panel */}
        {showBulkAssign && (
          <div className="mb-4 p-5 rounded-xl bg-white border-2 border-[#7c3aed] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#7c3aed]/10 border-2 border-[#7c3aed]/20 flex items-center justify-center">
                <i className="fas fa-users-gear text-[#7c3aed] text-sm" />
              </div>
              <div>
                <h4 className="font-syne text-sm font-extrabold text-[#0b1e33] uppercase tracking-tight">
                  BULK ASSIGN TO MY TEAM
                </h4>
                <p className="text-[10px] text-[#475569] font-mono font-bold">
                  {selectedLeadIds.size > 0
                    ? `${selectedLeadIds.size} selected lead(s) will be distributed`
                    : `All ${paginatedLeads.length} lead(s) on this page will be distributed`}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-[9px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  HOW MANY LEADS (OPTIONAL)
                </label>
                <input
                  type="number"
                  value={bulkCount}
                  onChange={(e) => setBulkCount(e.target.value)}
                  placeholder={`Max: ${getLeadsToAssign().length}`}
                  min="1"
                  max={getLeadsToAssign().length}
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#7c3aed] placeholder:text-[#94a3b8]"
                />
              </div>
              <div>
                <label className="text-[9px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  SELECT TEAM MEMBERS ({selectedBulkStaff.length} selected)
                </label>
                <div className="max-h-[160px] overflow-y-auto bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl p-2 space-y-1">
                  {members.length === 0 && (
                    <p className="text-[10px] text-[#64748b] font-mono font-bold p-2">
                      No team members yet
                    </p>
                  )}
                  {members.map((m) => (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#7c3aed]/5 transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBulkStaff.includes(m.id)}
                        onChange={() => toggleBulkStaff(m.id)}
                        className="w-4 h-4 rounded border-2 border-[#cbd5e1] text-[#7c3aed] focus:ring-[#7c3aed] cursor-pointer"
                      />
                      <span className="text-[11px] font-extrabold text-[#0f172a]">
                        {m.firstName} {m.lastName}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkAssign}
                disabled={
                  bulkAssigning ||
                  getLeadsToAssign().length === 0 ||
                  selectedBulkStaff.length === 0
                }
                className="px-6 py-2.5 rounded-xl bg-[#7c3aed] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#6d28d9] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-b-[2px] border-[#6d28d9] flex items-center gap-2"
              >
                {bulkAssigning ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    ASSIGNING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" /> ASSIGN{" "}
                    {getLeadsToAssignCount()} LEAD
                    {getLeadsToAssignCount() !== 1 ? "S" : ""}
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowBulkAssign(false);
                  setSelectedBulkStaff([]);
                  setBulkCount("");
                }}
                className="px-5 py-2.5 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] transition-all"
              >
                CANCEL
              </button>
            </div>
          </div>
        )}

        {/* Select-all bar */}
        {paginatedLeads.length > 0 && (
          <div className="flex items-center gap-3 px-5 py-2 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-t-xl">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAllPage}
                className="w-4 h-4 rounded border-2 border-[#cbd5e1] text-[#1e3a5f] focus:ring-[#1e3a5f] cursor-pointer"
              />
              <span className="text-[10px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                Select All on Page
              </span>
            </label>
            {selectedLeadIds.size > 0 && (
              <span className="text-[10px] text-[#1e3a5f] font-mono font-extrabold">
                ({selectedLeadIds.size} selected)
              </span>
            )}
          </div>
        )}

        {/* Leads list */}
        {displayLeads.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-[#cbd5e1] rounded-xl">
            <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-inbox text-[#94a3b8] text-2xl" />
            </div>
            <p className="text-[11px] text-[#64748b] font-mono font-bold">
              No leads match the current filters.
            </p>
          </div>
        ) : (
          <div className="divide-y-2 divide-[#f1f5f9] border-2 border-[#e2e8f0] border-t-0 rounded-b-xl overflow-hidden">
            {paginatedLeads.map((lead) => {
              const isSelected = selectedLeadIds.has(lead.id);
              const isAssigned = !!lead.assignedTo;
              const phones = lead.phones || [];
              return (
                <div
                  key={lead.id}
                  className={`p-4 transition-all ${isSelected ? "bg-[#f0fdfa]" : "bg-white hover:bg-[#f8fafc]"}`}
                >
                  <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleLead(lead.id)}
                        className="w-4 h-4 rounded border-2 border-[#cbd5e1] text-[#1e3a5f] focus:ring-[#1e3a5f] cursor-pointer"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div
                        className="flex items-center gap-2 mb-1 flex-wrap cursor-pointer"
                        onClick={() =>
                          setExpandedLead(
                            expandedLead === lead.id ? null : lead.id,
                          )
                        }
                      >
                        <span className="font-syne text-sm font-extrabold text-[#0f172a]">
                          {lead.name}
                        </span>
                        {lead.short_id && (
                          <span className="text-[10px] font-mono font-extrabold text-[#1e3a5f] bg-[#eff6ff] border-2 border-[#bfdbfe] px-2 py-0.5 rounded-lg">
                            #{lead.short_id}
                          </span>
                        )}
                        {getStatusBadge(lead.status)}
                        {isAssigned ? (
                          <span className="text-[9px] font-mono font-extrabold text-[#059669] bg-[#ecfdf5] border border-[#a7f3d0] px-2 py-0.5 rounded-lg">
                            <i className="fas fa-user-check mr-1" />
                            {lead.assignedFirstName
                              ? `${lead.assignedFirstName} ${lead.assignedLastName}`
                              : "With My Team"}
                          </span>
                        ) : (
                          <span className="text-[9px] font-mono font-extrabold text-[#1e3a5f] bg-[#eff6ff] border border-[#bfdbfe] px-2 py-0.5 rounded-lg">
                            <i className="fas fa-user-tie mr-1" />
                            {tlName ? `${tlName} TL` : "TEAM LEAD"}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#64748b] font-mono font-bold flex items-center gap-1 flex-wrap">
                        <span>{lead.email}</span>
                        <span>·</span>
                        <div className="flex items-center gap-1">
                          <span>{phones.map((p) => p.phone).join(" / ")}</span>
                          <button
                            onClick={(e) => handleDialNumber(lead.id, e)}
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0] hover:bg-[#d1fae5] transition-all font-extrabold ml-2"
                            title="Click to Call"
                          >
                            <i className="fas fa-phone-alt text-[9px]" /> CALL
                          </button>
                          <button
                            onClick={(e) => handleOpenSenderSelect(lead.id, lead.email, lead.name, e)}
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#eff6ff] text-[#2563eb] border border-[#bfdbfe] hover:bg-[#dbeafe] transition-all font-extrabold ml-1"
                            title="Click to Email"
                          >
                            <i className="fas fa-envelope text-[9px]" /> EMAIL
                          </button>
                        </div>
                        <span>· Created {formatDate(lead.createdAt)}</span>
                      </div>

                      {/* Expanded detail row */}
                      {expandedLead === lead.id && (
                        <div className="mt-3 pt-3 border-t-2 border-[#e2e8f0] space-y-3">
                          <div className="flex flex-wrap gap-4 text-[10px] text-[#475569] font-mono font-bold">
                            <span>
                              <i className="fas fa-tag mr-1 text-[#1e3a5f]" />
                              ID: {lead.short_id || "—"}
                            </span>
                            {lead.linked_user_id && (
                              <span>
                                <i className="fas fa-link mr-1 text-[#059669]" />
                                Linked User
                              </span>
                            )}
                            {lead.depositStatus === "deposited" && (
                              <span>
                                <i className="fas fa-coins mr-1 text-[#d97706]" />
                                Deposited
                              </span>
                            )}
                          </div>

                          {/* User Account Section */}
                          <div className="p-4 rounded-xl bg-[#ecfdf5] border-2 border-[#a7f3d0]">
                            <div className="text-[11px] text-[#059669] font-mono font-extrabold mb-3 flex items-center gap-2 uppercase tracking-wider">
                              <i className="fas fa-user-plus" /> USER ACCOUNT
                            </div>
                            {!lead.linked_user_id ? (
                              <div>
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() =>
                                      setShowPasswordField({
                                        ...showPasswordField,
                                        [lead.id]: !showPasswordField[lead.id],
                                      })
                                    }
                                    className="px-4 py-2 rounded-xl bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all border-b-[2px] border-[#047857]"
                                  >
                                    <i className="fas fa-user-plus mr-1.5" />{" "}
                                    CREATE ACCOUNT
                                  </button>
                                  <span className="text-[10px] text-[#475569] font-mono font-bold">
                                    Set a password to create the user account
                                  </span>
                                </div>
                                {showPasswordField[lead.id] && (
                                  <div className="flex items-center gap-2 mt-3">
                                    <input
                                      type="text"
                                      value={passwordInputs[lead.id] || ""}
                                      onChange={(e) =>
                                        setPasswordInputs({
                                          ...passwordInputs,
                                          [lead.id]: e.target.value,
                                        })
                                      }
                                      placeholder="Set password (min 6 chars)"
                                      className="bg-white border-2 border-[#a7f3d0] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#059669] w-56"
                                    />
                                    <button
                                      onClick={() =>
                                        handleConvertToUser(lead.id)
                                      }
                                      disabled={converting[lead.id]}
                                      className="px-4 py-2.5 rounded-xl bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] disabled:opacity-50 transition-all border-b-[2px] border-[#047857]"
                                    >
                                      {converting[lead.id] ? (
                                        <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        "CONFIRM & CREATE"
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={() =>
                                    window.open(
                                      `/admin/user/${lead.linked_user_id}`,
                                      "_blank",
                                    )
                                  }
                                  className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                                >
                                  <i className="fas fa-external-link-alt mr-1.5" />{" "}
                                  VIEW FULL USER PAGE
                                </button>
                                <button
                                  onClick={() =>
                                    setShowPasswordField({
                                      ...showPasswordField,
                                      [lead.id]: !showPasswordField[lead.id],
                                    })
                                  }
                                  className="px-4 py-2 rounded-xl bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b45309] transition-all border-b-[2px] border-[#b45309]"
                                >
                                  <i className="fas fa-key mr-1.5" /> RESET
                                  PASSWORD
                                </button>
                                {showPasswordField[lead.id] && (
                                  <div className="flex items-center gap-2 w-full mt-2">
                                    <input
                                      type="text"
                                      value={passwordInputs[lead.id] || ""}
                                      onChange={(e) =>
                                        setPasswordInputs({
                                          ...passwordInputs,
                                          [lead.id]: e.target.value,
                                        })
                                      }
                                      placeholder="New password (min 6 chars)"
                                      className="bg-white border-2 border-[#fde047] rounded-xl px-4 py-2.5 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#d97706] w-56"
                                    />
                                    <button
                                      onClick={() =>
                                        handleResetPassword(lead.id)
                                      }
                                      disabled={converting[lead.id]}
                                      className="px-4 py-2.5 rounded-xl bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b45309] disabled:opacity-50 transition-all border-b-[2px] border-[#b45309]"
                                    >
                                      {converting[lead.id] ? (
                                        <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        "UPDATE PASSWORD"
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Lead Type Toggle (Move to Retention) */}
                          <div className="p-4 rounded-xl bg-[#f8fafc] border-2 border-[#e2e8f0]">
                            <div className="text-[11px] text-[#475569] font-mono font-extrabold mb-3 flex items-center gap-2 uppercase tracking-wider">
                              <i className="fas fa-tag" /> LEAD TYPE
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() =>
                                  requestSetLeadType(lead, "conversion")
                                }
                                className={`px-4 py-2 rounded-xl text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-b-[2px] ${(lead.lead_type || "conversion") === "conversion" ? "bg-[#059669] text-white border-[#047857]" : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#059669] hover:text-[#059669]"}`}
                              >
                                <i className="fas fa-rotate mr-1.5" />{" "}
                                CONVERSION
                              </button>
                              <button
                                onClick={() =>
                                  requestSetLeadType(lead, "retention")
                                }
                                className={`px-4 py-2 rounded-xl text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-b-[2px] ${lead.lead_type === "retention" ? "bg-[#d97706] text-white border-[#b45309]" : "bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#d97706] hover:text-[#d97706]"}`}
                              >
                                <i className="fas fa-lock mr-1.5" /> RETENTION
                              </button>
                            </div>
                          </div>

                          {lead.comments && lead.comments.length > 0 && (
                            <div className="space-y-1.5">
                              <div className="text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                                HISTORY
                              </div>
                              {lead.comments.slice(-3).map((c) => (
                                <div
                                  key={c.id}
                                  className="flex items-start gap-2 text-[10px] bg-[#f8fafc] rounded-lg p-2 border border-[#e2e8f0]"
                                >
                                  <span className="text-[#1e3a5f] font-extrabold whitespace-nowrap">
                                    {c.staffName}:
                                  </span>
                                  <span className="text-[#475569] font-bold">
                                    {c.comment}
                                  </span>
                                  {c.status && (
                                    <span className="text-[#64748b] ml-auto whitespace-nowrap font-bold">
                                      →{" "}
                                      {c.status
                                        .replace(/_/g, " ")
                                        .toUpperCase()}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Status update form */}
                          <div className="flex items-start gap-2">
                            <select
                              value={statusInputs[lead.id] || ""}
                              onChange={(e) =>
                                setStatusInputs((prev) => ({
                                  ...prev,
                                  [lead.id]: e.target.value,
                                }))
                              }
                              className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] w-[140px] cursor-pointer flex-shrink-0"
                            >
                              <option value="">STATUS...</option>
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                  {s.replace(/_/g, " ").toUpperCase()}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={commentInputs[lead.id] || ""}
                              onChange={(e) =>
                                setCommentInputs((prev) => ({
                                  ...prev,
                                  [lead.id]: e.target.value,
                                }))
                              }
                              placeholder="Comment..."
                              className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] placeholder:text-[#94a3b8]"
                            />
                            <button
                              onClick={() => handleUpdateLead(lead.id)}
                              disabled={updating[lead.id]}
                              className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] disabled:opacity-50 transition-all border-b-[2px] border-[#0b1e33] whitespace-nowrap"
                            >
                              {updating[lead.id] ? (
                                <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                "UPDATE"
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Assign-down controls */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <select
                        value={selectedStaff[lead.id] || ""}
                        onChange={(e) =>
                          setSelectedStaff((prev) => ({
                            ...prev,
                            [lead.id]: e.target.value,
                          }))
                        }
                        className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] cursor-pointer"
                      >
                        <option value="">— my team —</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.firstName} {m.lastName}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleAssignDown(lead.id)}
                        disabled={assigning[lead.id] || !selectedStaff[lead.id]}
                        className="px-3 py-2 rounded-xl bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap border-b-[2px] border-[#047857]"
                      >
                        {assigning[lead.id] ? (
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "ASSIGN"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination />
      </div>

      {/* ─── TEAM POPUP ─── */}
      {showTeamPopup && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowTeamPopup(false)}
        >
          <div
            className="relative bg-white border-2 border-[#e2e8f0] rounded-2xl w-full max-w-[520px] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b-2 border-[#e2e8f0] flex items-center justify-between bg-[#f8fafc]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0d9488] to-[#0f766e] shadow-md shadow-[#0d9488]/25 flex items-center justify-center">
                  <i className="fas fa-users text-white text-sm" />
                </div>
                <div>
                  <h3 className="font-syne text-base font-extrabold text-[#0b1e33] uppercase tracking-tight">
                    MY TEAM
                  </h3>
                  <p className="text-[10px] text-[#475569] font-mono font-bold">
                    {members.length} staff member
                    {members.length !== 1 ? "s" : ""} assigned by Admin
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowTeamPopup(false)}
                className="w-9 h-9 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#dc2626] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-all flex items-center justify-center"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="p-5 max-h-[400px] overflow-y-auto">
              {members.length === 0 ? (
                <div className="text-center py-8 bg-[#f8fafc] border-2 border-dashed border-[#cbd5e1] rounded-xl">
                  <div className="w-14 h-14 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-3">
                    <i className="fas fa-users-slash text-[#94a3b8] text-xl" />
                  </div>
                  <p className="text-[11px] text-[#64748b] font-mono font-bold">
                    No team members assigned to you yet.
                  </p>
                  <p className="text-[10px] text-[#94a3b8] font-mono font-bold mt-1">
                    Contact Admin to get your team.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map((m) => (
                    <div
                      key={m.id}
                      className="bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl p-3 flex items-center gap-3 hover:border-[#0d9488]/30 hover:bg-white transition-all"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                        <span className="text-[12px] font-extrabold text-white">
                          {m.firstName?.[0] || ""}
                          {m.lastName?.[0] || ""}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-extrabold text-[#0f172a] truncate">
                          {m.firstName} {m.lastName}
                        </div>
                        <div className="text-[10px] text-[#64748b] font-mono font-bold truncate">
                          {m.email}
                        </div>
                      </div>
                      {m.staffType && (
                        <span className="text-[8px] font-mono font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-lg bg-[#0d9488]/10 text-[#0d9488] border border-[#0d9488]/20">
                          {m.staffType}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-3 bg-[#f8fafc] border-t-2 border-[#e2e8f0] flex justify-end">
              <button
                onClick={() => setShowTeamPopup(false)}
                className="px-5 py-2 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] transition-all"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CONFIRM MOVE MODAL ─── */}
      {confirmMove && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => {
            if (!confirmLoading) setConfirmMove(null);
          }}
        >
          <div
            className="relative bg-white border-2 border-[#e2e8f0] rounded-2xl w-full max-w-[440px] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b-2 border-[#e2e8f0] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-md ${confirmMove.targetType === "retention" ? "bg-gradient-to-br from-[#d97706] to-[#b45309] shadow-[#d97706]/25" : "bg-gradient-to-br from-[#059669] to-[#047857] shadow-[#059669]/25"}`}
                >
                  <i
                    className={`text-white text-sm ${confirmMove.targetType === "retention" ? "fas fa-lock" : "fas fa-rotate"}`}
                  />
                </div>
                <div>
                  <h3 className="font-syne text-base font-extrabold text-[#0b1e33] uppercase tracking-tight">
                    {confirmMove.targetType === "retention"
                      ? "Move to Retention"
                      : "Move to Conversion"}
                  </h3>
                  <p className="text-[10px] text-[#475569] font-mono font-bold">
                    {confirmMove.targetType === "retention"
                      ? "This action moves the lead to the retention desk"
                      : "This action moves the lead to the conversion desk"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (!confirmLoading) setConfirmMove(null);
                }}
                disabled={confirmLoading}
                className="w-9 h-9 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#dc2626] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-all flex items-center justify-center disabled:opacity-40"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-[12px] text-[#0f172a] font-bold leading-relaxed">
                {confirmMove.targetType === "retention"
                  ? "Are you sure you want to move this lead to Retention? It will be removed from your desk and transferred to the retention team."
                  : "Are you sure you want to move this lead back to Conversion? It will return to the AnzoTrade Leads page."}
              </p>
            </div>
            <div className="px-6 py-4 bg-[#f8fafc] border-t-2 border-[#e2e8f0] flex gap-3">
              <button
                onClick={() => setConfirmMove(null)}
                disabled={confirmLoading}
                className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] disabled:opacity-50 transition-all"
              >
                CANCEL
              </button>
              <button
                onClick={confirmMoveLead}
                disabled={confirmLoading}
                className={`flex-1 py-3 rounded-xl text-white text-[10px] font-syne font-extrabold uppercase tracking-wider transition-all border-b-[3px] flex items-center justify-center gap-2 ${confirmMove.targetType === "retention" ? "bg-[#d97706] hover:bg-[#b45309] border-[#b45309]" : "bg-[#059669] hover:bg-[#047857] border-[#047857]"} disabled:opacity-50`}
              >
                {confirmLoading ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    MOVING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check text-[9px]" />{" "}
                    {confirmMove.targetType === "retention"
                      ? "YES, MOVE TO RETENTION"
                      : "YES, MOVE TO CONVERSION"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── INBOX BUTTON & POPUP ─── */}
      <button onClick={handleOpenInbox} className="fixed bottom-6 right-24 z-[9990] w-14 h-14 rounded-full bg-[#7c3aed] text-white flex items-center justify-center shadow-xl hover:bg-[#6d28d9] transition-all border-b-4 border-[#6d28d9]" title="Inbox">
        <i className="fas fa-inbox text-xl" />
      </button>

      {showInbox && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowInbox(false)}>
          <div className="bg-white w-full max-w-4xl h-[80vh] rounded-2xl shadow-2xl overflow-hidden border-2 border-[#e2e8f0] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 bg-[#0b1e33] text-white flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-syne text-lg font-extrabold uppercase tracking-tight">Inbox</h3>
                <p className="text-[10px] font-mono text-[#bfdbfe] mt-0.5">Replies from your assigned leads</p>
              </div>
              <button onClick={() => setShowInbox(false)} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            
            <div className="flex-1 overflow-hidden flex">
              {/* List */}
              <div className="w-1/3 border-r-2 border-[#e2e8f0] overflow-y-auto bg-white">
                {inboxLoading ? (
                  <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#7c3aed] border-t-transparent rounded-full animate-spin" /></div>
                ) : inboxEmails.length === 0 ? (
                  <div className="text-center py-12 text-[#64748b]">
                    <i className="fas fa-envelope-open text-3xl mb-3 opacity-30" />
                    <p className="font-syne font-extrabold uppercase tracking-wider text-xs">No emails</p>
                  </div>
                ) : (
                  inboxEmails.map((em) => (
                    <div key={em.uid} onClick={() => handleViewEmailBody(em.uid)} className={`p-4 border-b border-[#f1f5f9] cursor-pointer hover:bg-[#f8fafc] transition-all ${selectedEmailBody?.subject === em.subject ? 'bg-[#f0fdfa] border-l-4 border-l-[#0d9488]' : ''}`}>
                      <div className="font-syne text-sm font-extrabold text-[#0f172a] truncate">
                        {em.lead?.name}
                        {em.lead?.short_id && <span className="ml-2 text-[9px] text-[#1e3a5f] bg-[#eff6ff] px-1.5 py-0.5 rounded border border-[#bfdbfe]">#{em.lead.short_id}</span>}
                      </div>
                      <div className="text-[10px] text-[#64748b] font-mono truncate">{em.subject}</div>
                      <div className="text-[9px] text-[#94a3b8] font-mono mt-1">{new Date(em.date).toLocaleDateString()}</div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Body */}
              <div className="w-2/3 p-6 overflow-y-auto bg-[#f8fafc]">
                {selectedEmailBody ? (
                  <div className="bg-white p-6 rounded-xl border-2 border-[#e2e8f0] shadow-sm">
                    <h4 className="font-syne text-lg font-extrabold text-[#0f172a] mb-2">{selectedEmailBody.subject}</h4>
                    <p className="text-xs text-[#475569] font-bold mb-4 border-b border-[#e2e8f0] pb-2">
                      From: {inboxEmails.find(e => e.subject === selectedEmailBody.subject)?.lead?.name || "Unknown Lead"}
                    </p>
                    <div className="text-sm text-[#0f172a]" dangerouslySetInnerHTML={{ __html: selectedEmailBody.html }} />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[#94a3b8] font-mono text-sm">
                    <div className="text-center">
                      <i className="fas fa-envelope-open-text text-4xl mb-3 opacity-20" />
                      <p>Select an email to view</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── RECENT CALLS BUTTON & POPUP ─── */}
      <button
        onClick={() => setShowCallHistory(true)}
        className="fixed bottom-6 right-6 z-[9990] w-14 h-14 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center shadow-xl hover:bg-[#0b1e33] transition-all border-b-4 border-[#0b1e33]"
        title="Recent Calls"
      >
        <i className="fas fa-history text-xl" />
      </button>

      {showCallHistory && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowCallHistory(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border-2 border-[#e2e8f0] animate-bounce-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 bg-[#0b1e33] text-white flex items-center justify-between">
              <div>
                <h3 className="font-syne text-lg font-extrabold uppercase tracking-tight">
                  Recent Incoming Calls
                </h3>
                <p className="text-[10px] font-mono text-[#bfdbfe] mt-0.5">
                  No numbers shown for privacy
                </p>
              </div>
              <button
                onClick={() => setShowCallHistory(false)}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
              >
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {callHistory.length === 0 ? (
                <div className="text-center py-12 text-[#64748b]">
                  <i className="fas fa-phone-slash text-3xl mb-3 opacity-30" />
                  <p className="font-syne font-extrabold uppercase tracking-wider">
                    No recent calls
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[#f1f5f9]">
                  {callHistory.map((call) => (
                    <div
                      key={call.id}
                      className="p-4 flex items-center justify-between hover:bg-[#f8fafc] transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${call.shortId ? "bg-[#eff6ff] text-[#1e3a5f]" : "bg-[#f1f5f9] text-[#94a3b8]"}`}
                        >
                          <i
                            className={`fas ${call.shortId ? "fa-user" : "fa-phone-slash"} text-sm`}
                          />
                        </div>
                        <div>
                          <div className="font-syne text-sm font-extrabold text-[#0f172a]">
                            {call.leadName}
                          </div>
                          <div className="text-[10px] font-mono font-bold text-[#64748b] flex items-center gap-2 mt-0.5">
                            <span>{call.timestamp}</span>
                            {call.shortId && (
                              <span className="text-[#059669] bg-[#ecfdf5] px-1.5 py-0.5 rounded border border-[#a7f3d0]">
                                #{call.shortId}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`text-[9px] font-mono font-extrabold uppercase px-2 py-1 rounded-lg ${call.shortId ? "bg-[#ecfdf5] text-[#059669]" : "bg-[#fef2f2] text-[#dc2626]"}`}
                        >
                          {call.shortId ? "Matched" : "Unknown"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 bg-[#f8fafc] border-t-2 border-[#e2e8f0] flex justify-end">
              <button
                onClick={() => setShowCallHistory(false)}
                className="px-6 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM INCOMING CALL WIDGET (no number shown) */}
      {incomingCall && (
        <div
          style={{ position: "fixed", bottom: 20, right: 20, zIndex: 10000 }}
          className="w-[320px] bg-white border-2 border-[#1e3a5f] rounded-2xl shadow-2xl overflow-hidden animate-bounce-in"
        >
          <div className="px-4 py-3 bg-[#0b1e33] text-white">
            <div className="text-[9px] font-mono font-extrabold uppercase tracking-wider text-[#a7f3d0] flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a7f3d0] animate-pulse" />{" "}
              INCOMING CALL
            </div>
            <div className="font-syne text-base font-extrabold mt-1 truncate">
              {incomingCall.lead?.name || "Unknown caller"}
            </div>
            {incomingCall.lead?.short_id && (
              <div className="text-[10px] font-mono font-bold text-[#bfdbfe]">
                #{incomingCall.lead.short_id}
              </div>
            )}
          </div>
          <div className="flex gap-2 p-3">
            <button
              onClick={declineCall}
              className="flex-1 py-2.5 rounded-xl bg-[#dc2626] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] border-b-[3px] border-[#b91c1c]"
            >
              <i className="fas fa-phone-slash mr-1.5" /> DECLINE
            </button>
            <button
              onClick={acceptCall}
              className="flex-1 py-2.5 rounded-xl bg-[#059669] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] border-b-[3px] border-[#047857]"
            >
              <i className="fas fa-phone-alt mr-1.5" /> ACCEPT
            </button>
          </div>
        </div>
      )}

      {/* CUSTOM END CALL BUTTON (outgoing calls) */}
      {isCallActive && !incomingCall && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
          <button
            onClick={handleHangup}
            className="px-6 py-3 rounded-full bg-[#dc2626] text-white font-syne font-extrabold uppercase tracking-wider shadow-2xl hover:bg-[#b91c1c] transition-all border-b-[3px] border-[#b91c1c] flex items-center gap-2"
          >
            <i className="fas fa-phone-slash text-lg" /> END CALL
          </button>
        </div>
      )}

      {/* SENDER SELECT POPUP */}
      {showSenderSelect && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowSenderSelect(false)}>
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden border-2 border-[#e2e8f0]" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 bg-[#1e3a5f] text-white flex items-center justify-between">
              <div>
                <h3 className="font-syne text-lg font-extrabold uppercase tracking-tight">Select Sender</h3>
                <p className="text-[10px] font-mono text-[#bfdbfe] mt-0.5">Choose which email to send from</p>
              </div>
              <button onClick={() => setShowSenderSelect(false)} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"><i className="fas fa-times text-sm" /></button>
            </div>
            <div className="p-4 space-y-2">
              {emailSenderOptions.map((opt) => (
                <button key={opt.id} onClick={() => handleSelectSender(opt)} className="w-full text-left px-4 py-3 rounded-xl border-2 border-[#e2e8f0] hover:border-[#1e3a5f] hover:bg-[#f8fafc] transition-all flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#eff6ff] flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-envelope text-[#1e3a5f] text-xs" />
                  </div>
                  <div className="font-mono text-sm font-extrabold text-[#0f172a]">{opt.name}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COMPOSE EMAIL MODAL (GMAIL STYLE) */}
      {showComposeEmail && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowComposeEmail(false)}>
          <div className="bg-white w-full max-w-[900px] h-[85vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-[#e2e8f0] animate-bounce-in" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="px-6 py-4 bg-[#f2f6fc] border-b border-[#e2e8f0] flex items-center justify-between flex-shrink-0">
              <h3 className="font-syne text-xl font-extrabold text-[#0f172a] tracking-tight flex items-center gap-3">
                <i className="fas fa-pen text-[#1e3a5f]" /> New Message
              </h3>
              <button onClick={() => setShowComposeEmail(false)} className="w-10 h-10 rounded-full hover:bg-[#e2e8f0] flex items-center justify-center transition-all text-[#64748b]">
                <i className="fas fa-times text-lg" />
              </button>
            </div>

            {/* Form Fields */}
            <div className="flex-1 overflow-y-auto flex flex-col">
              <div className="flex items-center border-b border-[#e2e8f0] px-6 py-3">
                <span className="text-sm text-[#64748b] w-20 font-medium">From</span>
                <div className="flex-1 text-sm font-bold text-[#0f172a] flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-[10px]">
                    {selectedSender?.name?.[0]?.toUpperCase()}
                  </div>
                  {selectedSender?.name}
                </div>
              </div>
              
              <div className="flex items-center border-b border-[#e2e8f0] px-6 py-3">
                <span className="text-sm text-[#64748b] w-20 font-medium">To</span>
                <div className="flex-1 text-sm font-bold text-[#0f172a] flex items-center gap-2">
                  <span className="bg-[#eff6ff] border border-[#bfdbfe] px-3 py-1 rounded-full text-[#1e3a5f] flex items-center gap-2">
                    {composeLeadName} 
                  </span>
                </div>
              </div>

              <div className="flex items-center border-b border-[#e2e8f0] px-6 py-3">
                <span className="text-sm text-[#64748b] w-20 font-medium">Subject</span>
                <input 
                  value={composeSubject} 
                  onChange={(e) => setComposeSubject(e.target.value)} 
                  placeholder="Subject" 
                  className="flex-1 text-sm font-bold text-[#0f172a] outline-none placeholder:text-[#94a3b8] placeholder:font-normal" 
                />
              </div>

              {/* Attachments Preview */}
              {attachments.length > 0 && (
                <div className="px-6 py-3 border-b border-[#e2e8f0] bg-[#f8fafc] flex flex-wrap gap-2">
                  {attachments.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white border border-[#cbd5e1] rounded-lg px-3 py-1.5 shadow-sm">
                      <i className={`fas ${file.type.includes('image') ? 'fa-image text-[#2563eb]' : 'fa-file-pdf text-[#dc2626]'} text-sm`} />
                      <span className="text-xs font-medium text-[#0f172a] max-w-[150px] truncate">{file.name}</span>
                      <span className="text-[9px] text-[#64748b] font-mono">{file.size}</span>
                      <button onClick={() => removeAttachment(idx)} className="ml-2 text-[#94a3b8] hover:text-[#dc2626]">
                        <i className="fas fa-times text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Toolbar */}
              <div className="flex items-center gap-1 px-6 py-2 border-b border-[#e2e8f0] bg-white flex-shrink-0">
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all"><i className="fas fa-bold text-sm" /></button>
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all"><i className="fas fa-italic text-sm" /></button>
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all"><i className="fas fa-underline text-sm" /></button>
                <div className="w-px h-6 bg-[#e2e8f0] mx-1" />
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all"><i className="fas fa-list-ul text-sm" /></button>
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all"><i className="fas fa-list-ol text-sm" /></button>
                <div className="w-px h-6 bg-[#e2e8f0] mx-1" />
                <button className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all"><i className="fas fa-link text-sm" /></button>
                <button onClick={() => fileInputRef.current?.click()} className="w-9 h-9 rounded hover:bg-[#f1f5f9] flex items-center justify-center text-[#475569] transition-all" title="Attach Image or PDF">
                  <i className="fas fa-paperclip text-sm" />
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  multiple 
                  accept="image/*,application/pdf" 
                  onChange={handleFileSelect} 
                  className="hidden" 
                />
              </div>

              <textarea 
                value={composeBody} 
                onChange={(e) => setComposeBody(e.target.value)} 
                placeholder="Write your message here..." 
                className="flex-1 w-full p-6 text-sm text-[#0f172a] outline-none resize-none placeholder:text-[#94a3b8] font-sans leading-relaxed" 
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-[#f2f6fc] border-t border-[#e2e8f0] flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleSendEmail} 
                  disabled={sendingEmail} 
                  className="px-8 py-3 rounded-full bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#0b1e33] disabled:opacity-50 transition-all flex items-center gap-2 shadow-md"
                >
                  {sendingEmail ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="fas fa-paper-plane" />}
                  Send
                </button>
                <button onClick={() => fileInputRef.current?.click()} className="w-10 h-10 rounded-full hover:bg-[#e2e8f0] flex items-center justify-center text-[#475569] transition-all" title="Attach file">
                  <i className="fas fa-paperclip" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowComposeEmail(false)} className="w-10 h-10 rounded-full hover:bg-[#e2e8f0] flex items-center justify-center text-[#475569] transition-all" title="Discard">
                  <i className="fas fa-trash-alt" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamLeadPanel;